import React, { Suspense, useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import TagManager from 'react-gtm-module';
import WonderNavigation from './components/wonder-navigation';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import gtmConfig from './config/gtm';
import { GameProvider } from './context/GameContext';
import { ComparisonModalManager } from './components/ComparisonModals';
import { WirelessAdvisorModalManager } from './components/WirelessAdvisor';
import RouterISPFeaturesModal from './components/ComparisonModals/RouterISPFeaturesModal';
import useComparisonStore from './components/ComparisonModals/store/comparisonStore';

// Lazy load pages
const Home = React.lazy(() => import('./pages/Home'));
const HowToSwitchPage = React.lazy(() => import('./pages/how-to-switch')); // Updated import path
const Coverage = React.lazy(() => import('./pages/Coverage'));
const PackagesPage = React.lazy(() => import('./pages/PackagesPage'));
const ComparePackagesPage = React.lazy(() => import('./pages/Compare'));
const RecommendPage = React.lazy(() => import('./pages/Recommend/index'));
const WonderWatch = React.lazy(() => import('./pages/wonderwatch'));
const WirelessAdvisor = React.lazy(() => import('./pages/WirelessAdvisor'));
const WhyChooseWondernet = React.lazy(() => import('./pages/why-choose-wondernet'));
const OurNetwork = React.lazy(() => import('./pages/our-network')); // Added route for Our Network page
const OurTeam = React.lazy(() => import('./pages/our-team')); // Added route for Our Team page
const ContactUs = React.lazy(() => import('./pages/contact-us')); // Added route for Contact Us page
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const AcceptableUsePolicy = React.lazy(() => import('./pages/AcceptableUsePolicy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const WirelessWonderPage = React.lazy(() => import('./pages/wireless-wonder')); // Added route for Wireless Wonder
const ResellerSupportPage = React.lazy(() => import('./pages/ResellerSupportPage')); // Added route for Reseller Support


// No game pages needed

// Promo date checker
const isPromoPeriod = () => {
  // Create date objects for SAST (UTC+2)
  const now = new Date();
  const startDate = new Date('2023-11-23T00:00:00+02:00'); // Start of promo period
  const endDate = new Date('2024-12-31T23:59:59+02:00'); // End of promo period
  
  // For testing - uncomment to always show promo
  // return true;
  
  // Check if the current date is within the promo period
  return now >= startDate && now <= endDate;
};

// Route change tracker component
const RouteChangeTracker = () => {
  const location = useLocation();
  const prevLocationRef = useRef(location);

  useEffect(() => {
    // Check if the location has actually changed
    if (
      prevLocationRef.current.pathname !== location.pathname ||
      prevLocationRef.current.search !== location.search
    ) {
      // Track page view in both GTM and Clarity with enhanced metadata
      import('./utils/analytics').then(analytics => {
        analytics.trackPageView(location.pathname, document.title, {
          previousPath: prevLocationRef.current.pathname,
          queryParams: location.search,
          isDeepLink: document.referrer.includes(window.location.host)
        });
      });
      
      // Update the previous location
      prevLocationRef.current = location;
    }
  }, [location]);

  return null;
};

// Modal opener component
const ModalOpener = ({ openComparisonModal, openRecommendationModal, openWirelessAdvisorModal }) => {
  const location = useLocation();
  useEffect(() => {
    // Check the current path and open the appropriate modal
    if (location.pathname === '/compare') {
      openComparisonModal();
    } else if (location.pathname === '/recommend') {
      openRecommendationModal();
    } else if (location.pathname === '/wireless-advisor') {
      openWirelessAdvisorModal();
    }
  }, [location, openComparisonModal, openRecommendationModal, openWirelessAdvisorModal]);
  
  return null;
};

const App = () => {
  // Get modal state and actions from Zustand store
  const {
    openSelectionModal,
    openRecommendationModal,
    isSelectionModalOpen,
    isComparisonModalOpen,
    isRecommendationModalOpen,
    closeAllModals
  } = useComparisonStore();
  
  // State for other modals
  const [isWirelessAdvisorModalOpen, setIsWirelessAdvisorModalOpen] = useState(false);
  const [isRouterISPFeaturesModalOpen, setIsRouterISPFeaturesModalOpen] = useState(false);
  const [routerISPFeaturesActiveTab, setRouterISPFeaturesActiveTab] = useState('router'); // 'router' or 'isp'
  
  useEffect(() => {
    // Initialize Google Tag Manager
    TagManager.initialize(gtmConfig);
  }, []);
  
  // Function to open the comparison modal with parameters
  const openComparisonModal = (params = {}) => {
    console.log('App.jsx: openComparisonModal called with params:', params);
    
    // Use the Zustand store to open the selection modal with parameters
    openSelectionModal(params);
  };
  
  // Function to open the wireless advisor modal
  const openWirelessAdvisorModal = () => {
    setIsWirelessAdvisorModalOpen(true);
  };
  
  // Function to open the router features modal
  const openRouterFeaturesModal = () => {
    setRouterISPFeaturesActiveTab('router');
    setIsRouterISPFeaturesModalOpen(true);
  };
  
  // Function to open the ISP features modal
  const openISPFeaturesModal = () => {
    setRouterISPFeaturesActiveTab('isp');
    setIsRouterISPFeaturesModalOpen(true);
  };
  
  // Make the openISPFeaturesModal function globally accessible
  useEffect(() => {
    window.openISPFeaturesModal = openISPFeaturesModal;
    
    // Cleanup function to remove the global reference when component unmounts
    return () => {
      delete window.openISPFeaturesModal;
    };
  }, []);

  // Reference to the WonderNavigation's openCoverageSidebar function
  const wonderNavigationRef = React.useRef(null);

  return (
    <HelmetProvider>
      <Router>
        <ErrorBoundary>
          <GameProvider>
            <div className="min-h-screen">
              <WonderNavigation
                ref={wonderNavigationRef}
                openComparisonModal={openComparisonModal}
                openRecommendationModal={openRecommendationModal}
                openWirelessAdvisorModal={openWirelessAdvisorModal}
                openRouterFeaturesModal={openRouterFeaturesModal}
                openISPFeaturesModal={openISPFeaturesModal}
              />
              <RouteChangeTracker />
              <ModalOpener
                openComparisonModal={openComparisonModal}
                openRecommendationModal={openRecommendationModal}
                openWirelessAdvisorModal={openWirelessAdvisorModal}
              />
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/switch" element={<HowToSwitchPage />} /> {/* Updated component name */}
                <Route path="/coverage" element={<Coverage />} />
                <Route path="/packages" element={<PackagesPage />} />
                {/* Redirect /compare to home page and open comparison modal */}
                <Route path="/compare" element={
                  <Navigate to="/" replace />
                } />
                
                {/* Redirect /recommend to home page and open recommendation modal */}
                <Route path="/recommend" element={
                  <Navigate to="/" replace />
                } />
                <Route path="/wonderwatch" element={<WonderWatch />} />
                <Route path="/wireless-advisor-page" element={<WirelessAdvisor />} />
                {/* Redirect /wireless-advisor to wireless-advisor-page and open wireless advisor modal */}
                <Route path="/wireless-advisor" element={
                  <Navigate to="/wireless-advisor-page" replace />
                } />
                <Route path="/why-choose-wondernet" element={<WhyChooseWondernet openComparisonModal={openComparisonModal} />} />
                <Route path="/our-network" element={<OurNetwork />} />
                <Route path="/our-team" element={<OurTeam />} />
                <Route path="/contact-us" element={<ContactUs openComparisonModal={openComparisonModal} />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
                
                <Route path="/wireless-wonder" element={<WirelessWonderPage />} />
                <Route path="/reseller-support" element={<ResellerSupportPage />} />

                {/* No game routes needed */}
                
                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <Footer />
              
              {/* Single ComparisonModalManager that handles both comparison and recommendation */}
              <ComparisonModalManager
                isOpen={isSelectionModalOpen || isComparisonModalOpen || isRecommendationModalOpen}
                onClose={closeAllModals}
                onSelectPackage={() => {}}
                openCoverageSidebar={() => {
                  // Call the openCoverageSidebar function from WonderNavigation
                  if (wonderNavigationRef.current && wonderNavigationRef.current.openCoverageSidebar) {
                    wonderNavigationRef.current.openCoverageSidebar();
                  }
                }}
              />
              
              {/* WirelessAdvisorModalManager */}
              <WirelessAdvisorModalManager
                isOpen={isWirelessAdvisorModalOpen}
                onClose={() => {
                  setIsWirelessAdvisorModalOpen(false);
                }}
              />
              
              {/* RouterISPFeaturesModal */}
              <RouterISPFeaturesModal
                isOpen={isRouterISPFeaturesModalOpen}
                onClose={() => {
                  setIsRouterISPFeaturesModalOpen(false);
                }}
                activeTab={routerISPFeaturesActiveTab}
              />
            </div>
          </GameProvider>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
};

export default App;